import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Truck, Store, Wand2, MapPin, ChevronLeft, ChevronRight, Smartphone, Apple, Bell, Play, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import StoreCard from "@/components/StoreCard";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const HERO_IMAGES = [
    "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=1400",
    "https://images.unsplash.com/photo-1559525839-d9acfd03b2ba?w=1400",
    "https://images.unsplash.com/photo-1541643600914-78b084683601?w=1400",
];

export default function Home() {
    const { t, lang, isRtl } = useLang();
    const { user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [popular, setPopular] = useState([]);
    const [newProducts, setNewProducts] = useState([]);
    const [featuredStores, setFeaturedStores] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [trending, setTrending] = useState([]);
    const [deals, setDeals] = useState([]);
    const [topRated, setTopRated] = useState([]);
    const [recommended, setRecommended] = useState([]);
    const [nearby, setNearby] = useState([]);
    const [cities, setCities] = useState([]);
    const [reels, setReels] = useState([]);
    const [selectedCity, setSelectedCity] = useState(() => localStorage.getItem("fm.city") || "riyadh");
    const [slide, setSlide] = useState(0);
    const [notifyEmail, setNotifyEmail] = useState("");

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
        api.get("/products/popular").then((r) => setPopular(r.data));
        api.get("/products/new").then((r) => setNewProducts(r.data));
        api.get("/stores/featured").then((r) => setFeaturedStores(r.data));
        api.get("/reviews/recent").then((r) => setReviews(r.data));
        api.get("/products/trending").then((r) => setTrending(r.data));
        api.get("/products/deals").then((r) => setDeals(r.data));
        api.get("/products/top-rated").then((r) => setTopRated(r.data));
        api.get("/products/recommended").then((r) => setRecommended(r.data));
        api.get("/cities").then((r) => setCities(r.data));
        api.get("/reels").then((r) => setReels(r.data));
    }, []);

    useEffect(() => {
        localStorage.setItem("fm.city", selectedCity);
        api.get("/products/nearby", { params: { city: selectedCity } }).then((r) => setNearby(r.data));
    }, [selectedCity]);

    // Auto-advance hero slider
    useEffect(() => {
        const iv = setInterval(() => setSlide((s) => (s + 1) % 3), 6000);
        return () => clearInterval(iv);
    }, []);

    const slides = [
        { title: t("heroSlide1Title"), sub: t("heroSlide1Sub"), img: HERO_IMAGES[0], badge: t("tagline") },
        { title: t("heroSlide2Title"), sub: t("heroSlide2Sub"), img: HERO_IMAGES[1], badge: t("todayDeals") },
        { title: t("heroSlide3Title"), sub: t("heroSlide3Sub"), img: HERO_IMAGES[2], badge: t("verified") },
    ];

    return (
        <Layout>
            {/* Hero slider */}
            <section className="relative overflow-hidden" data-testid="hero-slider">
                <div className="relative h-[600px] md:h-[680px]">
                    {slides.map((s, i) => (
                        <div
                            key={i}
                            className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                        >
                            <div className="absolute inset-0">
                                <img src={s.img} alt="" className="w-full h-full object-cover" />
                                <div className={`absolute inset-0 ${isRtl ? "bg-gradient-to-l from-white via-white/90 to-white/40" : "bg-gradient-to-r from-white via-white/90 to-white/40"}`} />
                            </div>
                            <div className="relative max-w-7xl mx-auto px-4 md:px-8 h-full flex items-center">
                                <div className="max-w-xl">
                                    <div className="inline-flex items-center gap-2 bg-white/80 border border-border rounded-full px-4 py-1.5 text-xs font-medium mb-6 shadow-sm">
                                        <Sparkles className="w-3.5 h-3.5 text-terracotta" />
                                        <span>{s.badge}</span>
                                    </div>
                                    <h1 className="hero-heading text-5xl sm:text-6xl lg:text-7xl text-espresso">{s.title}</h1>
                                    <p className="mt-6 text-lg text-muted-foreground max-w-lg leading-relaxed">{s.sub}</p>
                                    <div className="mt-8 flex flex-wrap items-center gap-3">
                                        <Link to="/products" className="inline-flex items-center gap-2 bg-terracotta text-white rounded-full px-7 py-3.5 font-bold hover:bg-terracotta/90 transition-colors" data-testid="hero-explore-btn">
                                            {t("exploreProducts")}
                                            <ArrowRight className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
                                        </Link>
                                        <Link to="/sell" className="inline-flex items-center gap-2 bg-white text-espresso border border-border rounded-full px-7 py-3.5 font-bold hover:bg-surface transition-colors" data-testid="hero-seller-btn">
                                            <Store className="w-4 h-4" />
                                            {t("openStore")}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {/* Slide controls */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setSlide(i)}
                                className={`h-2 rounded-full transition-all ${i === slide ? "w-8 bg-terracotta" : "w-2 bg-espresso/30"}`}
                                aria-label={`slide-${i}`}
                                data-testid={`hero-dot-${i}`}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Categories */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 md:-mt-16 relative z-10">
                <div className="bg-white rounded-3xl border border-border shadow-lg p-6 md:p-8" data-testid="categories-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase">{lang === "ar" ? "الفئات" : "Categories"}</span>
                            <h2 className="text-2xl md:text-3xl font-black text-espresso mt-1">{t("categoriesTitle")}</h2>
                        </div>
                        <Link to="/products" className="text-sm text-terracotta font-bold hover:underline">{t("seeAll")}</Link>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
                        {categories.slice(0, 14).map((c) => (
                            <Link key={c.category_id} to={`/products?category=${c.slug}`} className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-surface hover:bg-terracotta hover:text-white transition-colors border border-border" data-testid={`category-${c.slug}`}>
                                <div className="w-12 h-12 rounded-full bg-white grid place-items-center group-hover:bg-white/90 transition-colors">
                                    <span className="text-2xl">{categoryEmoji(c.slug)}</span>
                                </div>
                                <span className="text-xs font-bold text-center leading-tight">{lang === "ar" ? c.name_ar : c.name_en}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Today's deals */}
            {deals.length > 0 && (
                <Section title={t("todayDeals")} overline={lang === "ar" ? "لفترة محدودة" : "Limited time"} link="/products?sort=recent" testId="deals-section" accent="terracotta">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {deals.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* Marketplace Reels */}
            {reels.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20" data-testid="reels-section">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase flex items-center gap-1.5">
                                <Play className="w-3 h-3 fill-terracotta" />
                                {t("reels")}
                            </span>
                            <h2 className="text-2xl md:text-3xl font-black text-espresso mt-1">{t("reelsFeed")}</h2>
                        </div>
                        <Link to="/reels" className="text-sm text-terracotta font-bold hover:underline">{t("seeAll")}</Link>
                    </div>
                    <div className="flex gap-4 overflow-x-auto scroll-hide pb-2 -mx-4 md:-mx-8 px-4 md:px-8" data-testid="reels-strip">
                        {reels.slice(0, 8).map((r) => (
                            <Link
                                key={r.reel_id}
                                to={`/reels?r=${r.reel_id}`}
                                className="relative flex-shrink-0 w-40 md:w-52 aspect-[9/16] rounded-2xl overflow-hidden bg-espresso group border border-border"
                                data-testid={`reel-preview-${r.reel_id}`}
                            >
                                {r.thumbnail_url ? (
                                    <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                ) : (
                                    <video src={r.video_url} muted playsInline className="w-full h-full object-cover" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                                <Play className="absolute top-3 end-3 w-5 h-5 text-white fill-white" />
                                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                                    {r.store && <div className="text-xs font-bold opacity-90 line-clamp-1">{r.store.name}</div>}
                                    <div className="text-xs line-clamp-2 opacity-80 mt-0.5">{r.caption}</div>
                                    <div className="flex items-center gap-2 mt-2 text-[10px] opacity-80">
                                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{r.likes_count || 0}</span>
                                        <span>·</span>
                                        <span>{r.views || 0} {t("views")}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Best sellers */}
            <Section title={t("popularProducts")} overline={t("todayBest")} link="/products?sort=popular" testId="popular-section">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {popular.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                </div>
            </Section>

            {/* Featured stores */}
            <Section title={t("featuredSellers")} overline={lang === "ar" ? "مختارات" : "Handpicked"} link="/stores" testId="featured-stores-section">
                <div className="grid md:grid-cols-3 gap-6">
                    {featuredStores.slice(0, 3).map((s) => <StoreCard key={s.store_id} store={s} />)}
                </div>
            </Section>

            {/* Trending */}
            {trending.length > 0 && (
                <Section title={t("trending")} overline={lang === "ar" ? "الأكثر تصفحاً" : "Most viewed"} link="/products" testId="trending-section">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {trending.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* Products Near You */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20" data-testid="nearby-section">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {t("productsNearMe")}
                        </span>
                        <h2 className="text-2xl md:text-3xl font-black text-espresso mt-1">
                            {lang === "ar" ? "منتجات قريبة منك" : "Products near you"}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={selectedCity} onValueChange={setSelectedCity}>
                            <SelectTrigger className="w-40" data-testid="nearby-city-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {cities.map((c) => <SelectItem key={c.city_id} value={c.slug}>{lang === "ar" ? c.name_ar : c.name_en}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {nearby.length === 0 ? (
                    <div className="text-muted-foreground text-sm">{t("noResults")}</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {nearby.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                )}
            </section>

            {/* Top rated */}
            {topRated.length > 0 && (
                <Section title={t("topRated")} overline={lang === "ar" ? "الأعلى تقييماً" : "Highest rated"} link="/products?sort=popular" testId="top-rated-section">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {topRated.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* Recommended */}
            {recommended.length > 0 && (
                <Section title={t("recommended")} overline={user ? (lang === "ar" ? "مخصّص لك" : "For you") : (lang === "ar" ? "قد تعجبك" : "You may like")} link="/products" testId="recommended-section">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {recommended.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                    </div>
                </Section>
            )}

            {/* Recently added */}
            <Section title={t("newArrivals")} overline={t("recentlyAdded")} link="/products?sort=recent" testId="new-arrivals-section">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {newProducts.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
                </div>
            </Section>

            {/* Testimonials */}
            {reviews.length > 0 && (
                <Section title={t("testimonials")} overline={lang === "ar" ? "شهادات حقيقية" : "Real voices"} testId="reviews-section">
                    <div className="grid md:grid-cols-3 gap-5">
                        {reviews.slice(0, 3).map((r) => (
                            <div key={r.review_id} className="bg-white border border-border rounded-2xl p-6 shadow-sm">
                                <div className="text-ochre mb-3 text-lg">{"★".repeat(r.rating)}</div>
                                <p className="text-sm text-espresso leading-relaxed">{r.comment}</p>
                                <div className="mt-4 text-xs font-bold text-muted-foreground">— {r.user_name}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Why Choose Us */}
            <section className="mt-20 bg-gradient-to-br from-terracotta/5 to-ochre/5 py-16 md:py-24" data-testid="why-section">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase">
                            {lang === "ar" ? "لماذا نحن" : "Why us"}
                        </span>
                        <h2 className="text-3xl md:text-4xl font-black text-espresso mt-2">{t("whyChooseUs")}</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <ReasonCard icon={<ShieldCheck className="w-6 h-6" />} title={t("whyReason1Title")} body={t("whyReason1Body")} />
                        <ReasonCard icon={<Store className="w-6 h-6" />} title={t("whyReason2Title")} body={t("whyReason2Body")} />
                        <ReasonCard icon={<Truck className="w-6 h-6" />} title={t("whyReason3Title")} body={t("whyReason3Body")} />
                        <ReasonCard icon={<Wand2 className="w-6 h-6" />} title={t("whyReason4Title")} body={t("whyReason4Body")} />
                    </div>
                </div>
            </section>

            {/* Become a seller */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20" data-testid="become-seller-section">
                <div className="bg-espresso rounded-3xl p-10 md:p-16 relative overflow-hidden grain">
                    <div className="absolute inset-y-0 end-0 w-1/3 opacity-30 hidden md:block">
                        <img src="https://images.pexels.com/photos/8330227/pexels-photo-8330227.jpeg?w=800" alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-espresso" />
                    </div>
                    <div className="relative max-w-xl">
                        <span className="text-xs font-medium tracking-[0.2em] text-ochre uppercase">
                            {lang === "ar" ? "افتح متجرك" : "Sell with us"}
                        </span>
                        <h2 className="text-3xl md:text-5xl font-black text-white mt-2">{t("becomeSellerTitle")}</h2>
                        <p className="text-white/70 mt-4 text-lg leading-relaxed">{t("becomeSellerBody")}</p>
                        <Link to="/sell">
                            <Button className="mt-8 rounded-full h-12 px-8 bg-terracotta hover:bg-terracotta/90 text-white font-bold" data-testid="become-seller-btn">
                                {t("becomeSellerCta")}
                                <ArrowRight className={`w-4 h-4 ms-2 ${isRtl ? "rotate-180" : ""}`} />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Mobile App Coming Soon */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-16 mb-16" data-testid="app-coming-soon">
                <div className="bg-white border border-border rounded-3xl p-8 md:p-14 grid md:grid-cols-2 gap-8 items-center">
                    <div>
                        <span className="text-xs font-medium tracking-[0.2em] text-terracotta uppercase">{t("appComingSoon")}</span>
                        <h2 className="text-3xl md:text-4xl font-black text-espresso mt-2">
                            {lang === "ar" ? "تسوّق من جوالك" : "Shop from your phone"}
                        </h2>
                        <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">{t("appComingBody")}</p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md">
                            <Input
                                value={notifyEmail}
                                onChange={(e) => setNotifyEmail(e.target.value)}
                                placeholder={t("emailPlaceholder")}
                                type="email"
                                data-testid="notify-email-input"
                                className="flex-1 h-11 rounded-full px-5"
                            />
                            <Button
                                onClick={() => { if (notifyEmail) { toast.success("✓"); setNotifyEmail(""); } }}
                                className="h-11 rounded-full bg-terracotta text-white font-bold px-6"
                                data-testid="notify-me-btn"
                            >
                                <Bell className="w-4 h-4 me-1.5" />
                                {t("notifyMe")}
                            </Button>
                        </div>
                        <div className="mt-6 flex items-center gap-3 text-muted-foreground text-sm">
                            <div className="flex items-center gap-1.5 opacity-60"><Apple className="w-4 h-4" /> iOS</div>
                            <div className="flex items-center gap-1.5 opacity-60"><Smartphone className="w-4 h-4" /> Android</div>
                        </div>
                    </div>
                    <div className="relative h-64 md:h-80 flex items-center justify-center">
                        <div className="absolute w-64 h-64 md:w-80 md:h-80 rounded-full bg-gradient-to-br from-terracotta/20 to-ochre/30" />
                        <div className="relative w-48 h-96 md:w-56 md:h-[420px] bg-espresso rounded-[2.5rem] border-8 border-espresso shadow-2xl overflow-hidden">
                            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-espresso rounded-full z-10" />
                            <img src="https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?w=400" alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover opacity-90" />
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
}

function Section({ title, overline, link, testId, children, accent }) {
    const { t } = useLang();
    return (
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-20" data-testid={testId}>
            <div className="flex items-end justify-between mb-6">
                <div>
                    {overline && <span className={`text-xs font-medium tracking-[0.2em] uppercase ${accent === "terracotta" ? "text-terracotta" : "text-terracotta"}`}>{overline}</span>}
                    <h2 className="text-2xl md:text-3xl font-black text-espresso mt-1">{title}</h2>
                </div>
                {link && <Link to={link} className="text-sm text-terracotta font-bold hover:underline">{t("seeAll")}</Link>}
            </div>
            {children}
        </section>
    );
}

function ReasonCard({ icon, title, body }) {
    return (
        <div className="bg-white border border-border rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform transition-shadow duration-300">
            <div className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta grid place-items-center mb-4">
                {icon}
            </div>
            <h3 className="font-black text-espresso text-lg">{title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
        </div>
    );
}

function categoryEmoji(slug) {
    const map = {
        "homemade-food": "🍲", desserts: "🍪", cakes: "🎂", coffee: "☕",
        chocolate: "🍫", perfumes: "💐", "handmade-gifts": "🎁",
        candles: "🕯️", accessories: "💍", clothing: "👗", art: "🎨",
        flowers: "🌸", ramadan: "🌙", eid: "⭐",
    };
    return map[slug] || "✨";
}
